import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, CheckCircle, Calendar, Gauge, Heart } from 'lucide-react';

/**
 * VehicleResultCard - Enhanced vehicle card for results page
 * Cars.com-style layout with image, details, dealer info, and CTA
 */
const VehicleResultCard = ({ vehicle, distance = null, isFavorite = false, onToggleFavorite }) => {
  const navigate = useNavigate();

  // Format mileage
  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const handleBookTestDrive = () => {
    navigate(`/vehicle/${vehicle._id}`);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(vehicle._id);
    }
  };

  // Determine condition tags
  const tags = [];
  if (vehicle.featured) tags.push({ label: 'Featured', color: 'app-accent' });
  if (vehicle.mileage < 40000) tags.push({ label: 'Low Mileage', color: 'app-success' });
  if (vehicle.bookingCount && vehicle.bookingCount > 50) tags.push({ label: 'Popular Choice', color: 'app-warning' });

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border overflow-hidden hover:border-app-accent-light dark:hover:border-app-accent transition-all duration-200 hover:shadow-xl group">
      <div className="md:flex">
        {/* Vehicle Image - Left side on desktop */}
        <div className="relative md:w-80 h-56 md:h-auto bg-app-bg-tertiary-light dark:bg-app-bg-tertiary overflow-hidden flex-shrink-0">
          <img
            src={vehicle.image || 'https://via.placeholder.com/400x300?text=Vehicle+Image'}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
          />

          {/* Save/Favorite Button */}
          <button
            onClick={handleFavoriteClick}
            className="absolute top-3 right-3 p-2 bg-white/90 dark:bg-app-bg-secondary/90 backdrop-blur-sm rounded-full hover:scale-110 transition-all duration-200 shadow-lg"
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart
              className={`w-5 h-5 ${
                isFavorite
                  ? 'fill-app-error text-app-error'
                  : 'text-app-text-secondary-light dark:text-app-text-secondary'
              }`}
            />
          </button>

          {/* Condition Tags */}
          {tags.length > 0 && (
            <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
              {tags.map((tag, idx) => (
                <div
                  key={idx}
                  className={`bg-${tag.color} text-white px-3 py-1 rounded-lg text-xs font-semibold shadow-lg`}
                >
                  {tag.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Content - Right side on desktop */}
        <div className="flex-1 p-5 flex flex-col">
          {/* Vehicle Title */}
          <div className="mb-3">
            <h3 className="text-xl font-bold text-app-text-light dark:text-app-text mb-1 group-hover:text-app-accent transition-colors">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h3>
          </div>

          {/* Vehicle Stats */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-app-text-secondary-light dark:text-app-text-secondary">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{vehicle.year}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Gauge className="w-4 h-4" />
              <span>{formatMileage(vehicle.mileage)} miles</span>
            </div>
            {vehicle.type && (
              <div className="px-3 py-1 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary rounded-lg text-xs font-medium">
                {vehicle.type}
              </div>
            )}
            {vehicle.transmission && (
              <span className="text-xs">{vehicle.transmission}</span>
            )}
          </div>

          {/* Dealer Information */}
          {vehicle.dealer && (
            <div className="mb-4 pb-4 border-b border-app-border-light dark:border-app-border">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-app-accent flex-shrink-0" />
                    <span className="text-sm text-app-text-light dark:text-app-text font-semibold">
                      {vehicle.dealer.name}
                    </span>
                    {vehicle.dealer.verified && (
                      <CheckCircle className="w-4 h-4 text-app-success flex-shrink-0" />
                    )}
                  </div>

                  {/* Location & Distance */}
                  <div className="ml-6 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                    {vehicle.dealer.address && (
                      <span>
                        {vehicle.dealer.address.city}, {vehicle.dealer.address.state}
                      </span>
                    )}
                    {distance && (
                      <span className="ml-2 font-medium text-app-accent">
                        • {distance} miles away
                      </span>
                    )}
                  </div>
                </div>

                {/* Dealer Rating */}
                {vehicle.dealer.rating && vehicle.dealer.rating.count > 0 && (
                  <div className="flex items-center gap-1 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary px-3 py-1.5 rounded-lg">
                    <Star className="w-4 h-4 fill-app-warning text-app-warning" />
                    <span className="text-sm font-bold text-app-text-light dark:text-app-text">
                      {vehicle.dealer.rating.average.toFixed(1)}
                    </span>
                    <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                      ({vehicle.dealer.rating.count})
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom section: Availability + CTA */}
          <div className="mt-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Availability Indicator */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-app-success rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-app-success">
                Available for Test Drive
              </span>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleBookTestDrive}
              className="w-full sm:w-auto bg-app-accent hover:bg-app-accent/90 text-white px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg"
            >
              Book Test Drive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleResultCard;
